import React, { useMemo, useState } from 'react'
import { compose } from 'redux'
import PropTypes from 'prop-types'

import { apr } from '@cred/shared/formatters'

import { AdvertiserDisclosure } from 'components/advertiser-disclosure'
import WidgetPage from 'components/hocs/widget-page'
import { getMetaQuery } from 'utils/data'
import PoweredBy from 'components/powered-by'
import HeaderCTA from 'components/header-CTA'
import useActiveLenders from 'components/hooks/useActiveLenders'
import Superscript from 'components/RateTableFooterDisclosure/Superscript'
import RateTableFooterDisclosure from 'components/RateTableFooterDisclosure/Disclosure'
import { ADVERTISER_DISCLOSURE_THEMES } from 'common/constants'
import withRateScript from 'components/hocs/with-rate-script'
import { loadLendersRates } from 'common/utils/rates'
import withApolloProvider from 'components/hocs/withApolloProvider'
import FooterLinks from 'components/footer-links'

import { getCtaUrl } from './utils'
import ShortenedRateTableApp from './shortened-table'
import FullRateTableApp from './full-table'
import styles from './styles.module.scss'

const RateTableApp = (props) => {
  const {
    theme,
    variation,
    metaParams,
    verticals,
    type,
    embeddedWidgetQueryParams,
    isScriptLoaded,
  } = props

  const [isDisclosureOpen, setIsDisclosureOpen] = useState(false)

  const availableLenders = useActiveLenders('refi')
  const minRate = useMemo(() => {
    if (isScriptLoaded) {
      const filters = { vertical: 'refi', variable: true }
      const lendersWithRates = loadLendersRates({
        availableLenders,
        filters,
      })

      const { minFixedRate, minVariableRate } = lendersWithRates[0] || {}

      return Math.min(minFixedRate, minVariableRate)
    }

    return null
  }, [isScriptLoaded, availableLenders])

  const isFullTable = variation !== 'shortened'
  const showDisclosure = ADVERTISER_DISCLOSURE_THEMES.includes(theme)

  const metaQuery = getMetaQuery({ ...metaParams, marketplace: verticals, type }, theme)

  const ctaUrl = getCtaUrl({
    embeddedWidgetQueryParams,
  })

  return (
    <>
      {showDisclosure && <AdvertiserDisclosure tooltipId="fox-disclosure" theme={theme} />}
      <div className={styles.rates}>
        <div className={styles.headerAndFilters}>
          <PoweredBy stylesClassName="disclaimer" className={`mb-8 ${styles.poweredByDesktop}`} />
          <div className={styles.header}>
            <div>
              <p className={`mb-8 heading bold ${styles.headerTitle}`}>
                Get accurate refinancing options in 2 minutes
              </p>
              <p className={styles.description}>
                Compare up to {availableLenders?.length} lenders with rates starting at{' '}
                {apr(minRate) || ''} APR.
                <Superscript callback={() => setIsDisclosureOpen(true)} />
              </p>
            </div>
            <HeaderCTA ctaUrl={ctaUrl} blank />
          </div>
        </div>
        {isFullTable ? (
          <FullRateTableApp
            {...props}
            availableLenders={availableLenders}
            ctaUrl={ctaUrl}
            metaQuery={metaQuery}
          />
        ) : (
          <ShortenedRateTableApp
            {...props}
            availableLenders={availableLenders}
            ctaUrl={ctaUrl}
            metaQuery={metaQuery}
          />
        )}
        <FooterLinks containerClassName={styles.footerLinks} small />
        <PoweredBy stylesClassName="disclaimer" className={styles.poweredByMobile} />
        <RateTableFooterDisclosure
          slug="refi.landing.footer"
          isOpen={isDisclosureOpen}
          toggleDisclosure={() => setIsDisclosureOpen((prevOpen) => !prevOpen)}
        />
      </div>
    </>
  )
}

RateTableApp.propTypes = {
  isScriptLoaded: PropTypes.bool.isRequired,
  theme: PropTypes.string,
  variation: PropTypes.string,
  metaParams: PropTypes.object,
  verticals: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  type: PropTypes.string,
  variationMap: PropTypes.object,
  defaultVariationMap: PropTypes.object,
  embeddedWidgetQueryParams: PropTypes.string.isRequired,
}

// Export named class and functions for unit tests
export { RateTableApp }

export default compose(withApolloProvider, WidgetPage, withRateScript)(RateTableApp)
